import { useEffect, useId, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";

// An unknown key must never resolve to `undefined` — that leaves the panel at
// plain `w-full`, i.e. the full browser window. Callers passing "sm" hit exactly
// that, so the scale starts at sm and unknown keys fall back to `lg`.
const SIZES = { sm: "max-w-md", md: "max-w-lg", lg: "max-w-2xl", xl: "max-w-3xl" };

// Everything a Tab press can land on inside the dialog.
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Centered modal with a sticky header/footer and a SCROLLABLE body — built for
// large content (run details, long bug reports, big forms).
export default function Modal({ open, onClose, title, children, footer, size = "lg" }) {
  const panelRef = useRef(null);
  const restoreTo = useRef(null);
  const titleId = useId();

  // `onClose` is read through a ref so the effect below can depend on `open`
  // ALONE. Almost every caller passes an inline arrow (or a plain function
  // declared in its body), which is a new identity on every render — so with
  // onClose in the dependency array the whole effect tore down and re-ran on
  // each keystroke. Its cleanup calls restoreTo.focus(), which pulled focus out
  // of the field being typed into and put it back on the button that opened the
  // dialog: you could type exactly one character into any modal form.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Close on Escape; lock background scroll; keep focus inside the dialog and give
  // it back afterwards. Without the trap, Tab walked the still-tabbable table rows
  // and pager underneath the scrim, and closing dropped focus on <body> so the next
  // Tab restarted from the top of the document instead of the row you came from.
  useEffect(() => {
    if (!open) return undefined;
    restoreTo.current = document.activeElement;

    const onKey = (e) => {
      if (e.key === "Escape") { onCloseRef.current?.(); return; }
      if (e.key !== "Tab") return;
      const items = panelRef.current?.querySelectorAll(FOCUSABLE);
      if (!items || items.length === 0) { e.preventDefault(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      // Wrap at both ends, and pull focus back in if it has escaped the panel.
      if (!panelRef.current.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault(); first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    // Focus the panel itself rather than its first control, so a screen reader
    // announces the dialog and its title before reading any field.
    const focusTimer = setTimeout(() => panelRef.current?.focus(), 0);

    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      // Covers every exit — Escape, the X, a scrim click and an unmount.
      const target = restoreTo.current;
      if (target && typeof target.focus === "function" && document.contains(target)) {
        target.focus();
      }
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <div key="modal-root" className="fixed inset-0 z-[60] flex items-end justify-center p-0 sm:items-center sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
            // Plain translucent scrim — no `backdrop-blur` here: blurring the whole
            // (still-animating) page every frame is what made the open janky.
            className="absolute inset-0 bg-slate-900/60"
          />
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 460, damping: 34, mass: 0.7 }}
            style={{ willChange: "transform, opacity" }}
            className={cn(
              "relative flex max-h-[88vh] w-full flex-col overflow-hidden rounded-t-3xl bg-white shadow-glow outline-none dark:bg-ink-900 sm:rounded-2xl",
              SIZES[size] || SIZES.lg
            )}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-white/10">
              <h3 id={titleId} className="text-base font-semibold text-slate-900 dark:text-white">
                {title}
              </h3>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/5"
              >
                <X size={18} />
              </button>
            </div>

            {/* scrollable body */}
            <div className="flex-1 overflow-y-auto px-5 py-5">{children}</div>

            {footer && (
              <div className="border-t border-slate-200 px-5 py-3.5 dark:border-white/10">
                {footer}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
